import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { API_URL, LOCAL_API_URL, LIVE_API_URL } from '../Config';

// Action types
const VIDEO_GENERATION_ACTIONS = {
  START_GENERATION: 'START_GENERATION',
  UPDATE_PROGRESS: 'UPDATE_PROGRESS',
  COMPLETE_GENERATION: 'COMPLETE_GENERATION',
  FAIL_GENERATION: 'FAIL_GENERATION',
  MINIMIZE_MODAL: 'MINIMIZE_MODAL',
  MAXIMIZE_MODAL: 'MAXIMIZE_MODAL',
  CLEAR_GENERATION: 'CLEAR_GENERATION'
};

// Initial state
const initialState = {
  isGenerating: false,
  isModalMinimized: false,
  generationId: null,
  projectId: null,
  projectTitle: '',
  selectedLanguage: 'original',
  progress: 0,
  status: 'idle', // idle, pending, processing, completed, failed
  videoUrl: null,
  error: null,
  startTime: null,
  estimatedTimeRemaining: null
};

// Reducer
const videoGenerationReducer = (state, action) => {
  switch (action.type) {
    case VIDEO_GENERATION_ACTIONS.START_GENERATION:
      return {
        ...state,
        isGenerating: true,
        generationId: action.payload.generationId,
        projectId: action.payload.projectId,
        projectTitle: action.payload.projectTitle,
        selectedLanguage: action.payload.selectedLanguage || 'original',
        progress: 0,
        status: 'pending',
        videoUrl: null,
        error: null,
        startTime: Date.now(),
        estimatedTimeRemaining: null,
        isModalMinimized: false
      };
    
    case VIDEO_GENERATION_ACTIONS.UPDATE_PROGRESS:
      const newProgress = action.payload.progress;
      const elapsed = Date.now() - state.startTime;
      const estimatedTotal = newProgress > 0 ? (elapsed / newProgress) * 100 : null;
      const estimatedRemaining = estimatedTotal ? estimatedTotal - elapsed : null;
      
      return {
        ...state,
        progress: newProgress,
        status: action.payload.status || state.status,
        estimatedTimeRemaining: estimatedRemaining
      };
    
    case VIDEO_GENERATION_ACTIONS.COMPLETE_GENERATION:
      return {
        ...state,
        isGenerating: false,
        progress: 100,
        status: 'completed',
        videoUrl: action.payload.videoUrl,
        estimatedTimeRemaining: 0
      };
    
    case VIDEO_GENERATION_ACTIONS.FAIL_GENERATION:
      return {
        ...state,
        isGenerating: false,
        status: 'failed',
        error: action.payload.error,
        estimatedTimeRemaining: null
      };
    
    case VIDEO_GENERATION_ACTIONS.MINIMIZE_MODAL:
      return { ...state, isModalMinimized: true };
    
    case VIDEO_GENERATION_ACTIONS.MAXIMIZE_MODAL:
      return { ...state, isModalMinimized: false };
    
    case VIDEO_GENERATION_ACTIONS.CLEAR_GENERATION:
      return { ...initialState };
    
    default:
      return state;
  }
};

// Context
const VideoGenerationContext = createContext();

// Storage keys
const STORAGE_KEY = 'videoGeneration';

// Provider component
export const VideoGenerationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(videoGenerationReducer, initialState);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (state.isGenerating || state.status === 'completed' || state.status === 'failed') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [state]);

  // Load state from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem(STORAGE_KEY);
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        // Only restore if we were actually generating
        if (parsedState.isGenerating) {
          // Restore the full state
          Object.keys(parsedState).forEach(key => {
            if (key !== 'isGenerating') {
              // Set individual state properties
              dispatch({ 
                type: VIDEO_GENERATION_ACTIONS.START_GENERATION, 
                payload: {
                  generationId: parsedState.generationId,
                  projectId: parsedState.projectId,
                  projectTitle: parsedState.projectTitle,
                  selectedLanguage: parsedState.selectedLanguage
                }
              });
              
              if (parsedState.progress > 0) {
                dispatch({
                  type: VIDEO_GENERATION_ACTIONS.UPDATE_PROGRESS,
                  payload: { 
                    progress: parsedState.progress, 
                    status: parsedState.status 
                  }
                });
              }
            }
          });
          
          // Resume polling by checking if video is ready
          const expectedVideoPath = parsedState.selectedLanguage && parsedState.selectedLanguage !== 'original' 
            ? `/generated_videos/${parsedState.selectedLanguage}_project_${parsedState.projectId}_final.mp4`
            : `/generated_videos/project_${parsedState.projectId}_final.mp4`;
          
          const resumePolling = () => {
            fetch(`${API_URL}${expectedVideoPath}`, { method: 'HEAD' })
              .then(response => {
                if (response.ok) {
                  // Video is ready!
                  console.log('Resumed: Video generation completed, file found:', expectedVideoPath);
                  dispatch({
                    type: VIDEO_GENERATION_ACTIONS.COMPLETE_GENERATION,
                    payload: { videoUrl: expectedVideoPath }
                  });
                } else {
                  // Still processing, continue polling
                  const elapsed = Date.now() - parsedState.startTime;
                  const estimatedProgress = Math.min(90, Math.floor(elapsed / 3000));
                  
                  dispatch({
                    type: VIDEO_GENERATION_ACTIONS.UPDATE_PROGRESS,
                    payload: { 
                      progress: Math.max(parsedState.progress || 10, estimatedProgress), 
                      status: 'processing'
                    }
                  });
                  
                  setTimeout(resumePolling, 5000);
                }
              })
              .catch(error => {
                console.error('Error resuming generation check:', error);
                setTimeout(resumePolling, 5000);
              });
          };
          
          // Start resumed polling
          setTimeout(resumePolling, 2000);
        }
      } catch (error) {
        console.error('Error loading saved generation state:', error);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  // Polling function
  const pollGenerationStatus = useCallback(async (generationId) => {
    try {
      const response = await fetch(`${API_URL}/generation-status/${generationId}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.status === 'completed') {
        dispatch({
          type: VIDEO_GENERATION_ACTIONS.COMPLETE_GENERATION,
          payload: { videoUrl: data.videoUrl }
        });
        
        // Show browser notification
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('Video Generation Complete!', {
            body: `Video for "${state.projectTitle}" is ready for download.`,
            icon: '/favicon.ico'
          });
        }
        
        return false; // Stop polling
      } else if (data.status === 'failed') {
        dispatch({
          type: VIDEO_GENERATION_ACTIONS.FAIL_GENERATION,
          payload: { error: data.error || 'Generation failed' }
        });
        return false; // Stop polling
      } else {
        // Update progress
        dispatch({
          type: VIDEO_GENERATION_ACTIONS.UPDATE_PROGRESS,
          payload: { progress: data.progress || 0, status: data.status }
        });
        return true; // Continue polling
      }
    } catch (error) {
      console.error('Error polling generation status:', error);
      dispatch({
        type: VIDEO_GENERATION_ACTIONS.FAIL_GENERATION,
        payload: { error: error.message }
      });
      return false; // Stop polling
    }
  }, [state.projectTitle]);

  // Start polling with cleanup
  const startPolling = useCallback((generationId) => {
    const pollInterval = setInterval(async () => {
      const shouldContinue = await pollGenerationStatus(generationId);
      if (!shouldContinue) {
        clearInterval(pollInterval);
      }
    }, 3000); // Poll every 3 seconds

    // Cleanup on unmount or when polling stops
    return () => clearInterval(pollInterval);
  }, [pollGenerationStatus]);

  // Actions
  const startGeneration = useCallback(async (projectId, projectTitle, selectedLanguage = 'original', itemId = null) => {
    try {
      // Request notification permission
      if ('Notification' in window && Notification.permission === 'default') {
        await Notification.requestPermission();
      }

      const generationId = `${projectId}-${Date.now()}`;
      
      // Determine the expected video path based on whether this is individual clip or full project
      let expectedVideoPath;
      let apiEndpoint;
      let generationType;
      
      if (itemId) {
        // Individual clip generation
        expectedVideoPath = selectedLanguage && selectedLanguage !== 'original' 
          ? `/generated_videos/${selectedLanguage}_${itemId}.mp4`
          : `/generated_videos/${itemId}.mp4`;
        apiEndpoint = `${API_URL}/timeline-items/${itemId}/generate-video${selectedLanguage && selectedLanguage !== 'original' ? `?target_language_code=${selectedLanguage}` : ''}`;
        generationType = 'individual_clip';
        console.log(`Starting INDIVIDUAL clip video generation for item ${itemId} in project ${projectId} with language: ${selectedLanguage}`);
      } else {
        // Full project generation (existing behavior)
        expectedVideoPath = selectedLanguage && selectedLanguage !== 'original' 
          ? `/generated_videos/${selectedLanguage}_project_${projectId}_final.mp4`
          : `/generated_videos/project_${projectId}_final.mp4`;
        // Add force_regenerate=false to ensure existing videos are reused
        const langParam = selectedLanguage && selectedLanguage !== 'original' ? `target_language_code=${selectedLanguage}` : '';
        const forceParam = 'force_regenerate=false';
        const queryString = langParam ? `${langParam}&${forceParam}` : forceParam;
        apiEndpoint = `${LOCAL_API_URL}/projects/${projectId}/generate-video-with-data?${queryString}`;
        generationType = 'full_project';
        console.log(`Starting FULL PROJECT video generation for project ${projectId} with language: ${selectedLanguage}`);
      }

      // Update state to show generation started
      dispatch({
        type: VIDEO_GENERATION_ACTIONS.START_GENERATION,
        payload: { generationId, projectId, projectTitle, selectedLanguage, itemId, generationType }
      });

      // Warn user that backend will be busy during generation
      console.warn('⚠️  Starting video generation - backend will be unavailable for other requests during processing');
      
      // For individual clip generation, we need to load and send timeline configuration data
      let requestBody = undefined;
      if (itemId) {
        try {
          // Load timeline configuration for this specific item
          const configResponse = await fetch(`${API_URL}/timeline-items/${itemId}/timeline-export?language=${selectedLanguage || 'original'}`);
          if (configResponse.ok) {
            const timelineData = await configResponse.json();
            requestBody = JSON.stringify(timelineData);
            console.log(`🎵 Loaded timeline configuration for item ${itemId} in ${selectedLanguage} - has audio tracks: ${timelineData.timeline?.audioTrack?.length > 0}`);
          } else {
            console.warn(`⚠️ Could not load timeline configuration for item ${itemId} - proceeding with fallback`);
          }
        } catch (error) {
          console.error(`❌ Failed to load timeline configuration for item ${itemId}:`, error);
        }
      }
      
      // For full project generation using database-free endpoint, fetch data from live API first
      if (generationType === 'full_project' && apiEndpoint.includes('generate-video-with-data')) {
        console.log(`📡 Fetching project data from live API for project ${projectId}`);
        fetch(`${LIVE_API_URL}/projects/${projectId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        }).then(liveResponse => {
          if (!liveResponse.ok) {
            throw new Error(`Failed to fetch project data: ${liveResponse.statusText}`);
          }
          return liveResponse.json();
        }).then(projectData => {
          const timelineData = projectData.timeline_items || [];
          console.log(`✅ Fetched project data: ${projectData.title}, Timeline items: ${timelineData.length}`);
          
          // Now call local render API with live data
          requestBody = JSON.stringify({
            project: projectData,
            timeline: timelineData
          });
          
          console.log(`🚀 Calling local render API with live data: ${apiEndpoint}`);
          return fetch(apiEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: requestBody
          });
        }).then(response => {
          console.log(`Backend generation request sent, status: ${response.status}`);
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          return response.json();
        }).then(data => {
          console.log('✅ Backend generation completed:', data);
          console.log('✅ Backend is now available for other requests');
        }).catch(error => {
          console.error('❌ Backend generation failed:', error);
          dispatch({
            type: VIDEO_GENERATION_ACTIONS.FAIL_GENERATION,
            payload: { error: `Backend generation failed: ${error.message}` }
          });
        });
      } else {
        // For individual clip generation, use original behavior
        fetch(apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: requestBody
        }).then(response => {
          console.log(`Backend generation request sent, status: ${response.status}`);
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          return response.json();
        }).then(data => {
          console.log('✅ Backend generation completed:', data);
          console.log('✅ Backend is now available for other requests');
        }).catch(error => {
          console.error('❌ Backend generation failed:', error);
          dispatch({
            type: VIDEO_GENERATION_ACTIONS.FAIL_GENERATION,
            payload: { error: `Backend generation failed: ${error.message}` }
          });
        });
      }

      // Start polling for the output file
      const pollForCompletion = () => {
        // Check if video file exists by trying to fetch it
        fetch(`${API_URL}${expectedVideoPath}`, { method: 'HEAD' })
          .then(response => {
            if (response.ok) {
              // Video is ready!
              console.log('Video generation completed, file found:', expectedVideoPath);
              dispatch({
                type: VIDEO_GENERATION_ACTIONS.COMPLETE_GENERATION,
                payload: { videoUrl: expectedVideoPath }
              });
              
              // Show browser notification
              if ('Notification' in window && Notification.permission === 'granted') {
                const notificationBody = itemId 
                  ? `Individual clip video (item ${itemId}) for "${projectTitle}" is ready for download.`
                  : `Full project video for "${projectTitle}" is ready for download.`;
                new Notification('Video Generation Complete!', {
                  body: notificationBody,
                  icon: '/favicon.ico'
                });
              }
            } else {
              // Still processing, update progress and continue polling
              const elapsed = Date.now() - state.startTime;
              const estimatedProgress = Math.min(90, Math.floor(elapsed / 3000)); // Rough progress estimation
              
              dispatch({
                type: VIDEO_GENERATION_ACTIONS.UPDATE_PROGRESS,
                payload: { 
                  progress: Math.max(10, estimatedProgress), 
                  status: 'processing'
                }
              });
              
              // Continue polling
              setTimeout(pollForCompletion, 5000);
            }
          })
          .catch(error => {
            console.error('Error checking video status:', error);
            // Continue polling despite errors (might be network hiccup)
            setTimeout(pollForCompletion, 5000);
          });
      };

      // Start polling after initial delay
      setTimeout(pollForCompletion, 10000);

      return { success: true, generationId };
    } catch (error) {
      console.error('Error starting video generation:', error);
      dispatch({
        type: VIDEO_GENERATION_ACTIONS.FAIL_GENERATION,
        payload: { error: error.message }
      });
      return { success: false, error: error.message };
    }
  }, [state.startTime]);

  const minimizeModal = useCallback(() => {
    dispatch({ type: VIDEO_GENERATION_ACTIONS.MINIMIZE_MODAL });
  }, []);

  const maximizeModal = useCallback(() => {
    dispatch({ type: VIDEO_GENERATION_ACTIONS.MAXIMIZE_MODAL });
  }, []);

  const clearGeneration = useCallback(() => {
    dispatch({ type: VIDEO_GENERATION_ACTIONS.CLEAR_GENERATION });
  }, []);

  // Helper functions for individual clip generation
  const startIndividualClipGeneration = useCallback((projectId, projectTitle, selectedLanguage, itemId) => {
    const generationId = `${projectId}-${Date.now()}`;
    dispatch({
      type: VIDEO_GENERATION_ACTIONS.START_GENERATION,
      payload: { generationId, projectId, projectTitle, selectedLanguage, itemId, generationType: 'individual_clip' }
    });
    return generationId;
  }, []);

  const completeIndividualClipGeneration = useCallback((videoUrl) => {
    dispatch({
      type: VIDEO_GENERATION_ACTIONS.COMPLETE_GENERATION,
      payload: { videoUrl }
    });
  }, []);

  const failIndividualClipGeneration = useCallback((error) => {
    dispatch({
      type: VIDEO_GENERATION_ACTIONS.FAIL_GENERATION,
      payload: { error }
    });
  }, []);

  const value = {
    ...state,
    startGeneration,
    minimizeModal,
    maximizeModal,
    clearGeneration,
    startIndividualClipGeneration,
    completeIndividualClipGeneration,
    failIndividualClipGeneration
  };

  return (
    <VideoGenerationContext.Provider value={value}>
      {children}
    </VideoGenerationContext.Provider>
  );
};

// Custom hook
export const useVideoGeneration = () => {
  const context = useContext(VideoGenerationContext);
  if (!context) {
    throw new Error('useVideoGeneration must be used within a VideoGenerationProvider');
  }
  return context;
};